'use client';

import * as SubframeCore from '@subframe/core';
import React from 'react';
import { cn } from '../utils';

interface RadioCardProps extends React.ComponentProps<typeof SubframeCore.RadioGroup.Item> {
  disabled?: boolean;
  checked?: boolean;
  hideRadio?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const RadioCard = React.forwardRef<HTMLButtonElement, RadioCardProps>(function RadioCard(
  {
    disabled = false,
    checked, // Radix UI handles this internally via Context usually, but we keep it in props if passed explicitly
    hideRadio = false,
    children,
    className,
    ...otherProps
  }: RadioCardProps,
  ref,
) {
  return (
    <SubframeCore.RadioGroup.Item disabled={disabled} asChild={true} {...otherProps}>
      <button
        type="button"
        className={cn(
          'group/card flex w-full cursor-pointer items-start gap-4 rounded-lg border p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-50',
          // Estado Default (Não selecionado)
          'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50',
          // Estado Checked (Selecionado - via data-state do Radix)
          'data-[state=checked]:bg-brand-50 data-[state=checked]:border-brand-600 data-[state=checked]:ring-brand-600 data-[state=checked]:ring-1',
          className,
        )}
        ref={ref}
      >
        {!hideRadio ? (
          <div
            className={cn(
              'relative mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border transition-all',
              // Default
              'border-neutral-300 group-hover/card:border-neutral-400',
              // Checked (via data-state do pai - o botão)
              'group-data-[state=checked]/card:border-brand-600 group-data-[state=checked]/card:bg-brand-600',
            )}
          >
            <div
              className={cn(
                'h-2 w-2 scale-0 rounded-full bg-white transition-transform',
                // Checked (via data-state do pai)
                'group-data-[state=checked]/card:scale-100',
              )}
            />
          </div>
        ) : null}
        {children ? (
          <div className="flex min-w-0 grow basis-0 flex-col items-start gap-2">{children}</div>
        ) : null}
      </button>
    </SubframeCore.RadioGroup.Item>
  );
});

interface RadioCardGroupRootProps extends React.ComponentProps<
  typeof SubframeCore.RadioGroup.Root
> {
  children?: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

const RadioCardGroupRoot = React.forwardRef<HTMLDivElement, RadioCardGroupRootProps>(
  function RadioCardGroupRoot(
    { children, className, ...otherProps }: RadioCardGroupRootProps,
    ref,
  ) {
    return children ? (
      <SubframeCore.RadioGroup.Root asChild={true} {...otherProps}>
        <div className={cn('flex flex-wrap gap-2', className)} ref={ref}>
          {children}
        </div>
      </SubframeCore.RadioGroup.Root>
    ) : null;
  },
);

export const RadioCardGroup = Object.assign(RadioCardGroupRoot, {
  RadioCard,
});
